/**
 * Drill Plan types for the Automated Driller MVP.
 *
 * @see docs/features/260331-automated-driller-mvp/FEATURE_SPEC.md (Data model)
 * @see docs/features/260331-automated-driller-mvp/KICKOFF_PROMPT.md §1
 */

import type { DrillSuggestionFilters } from '@/lib/posture-analysis/drill-suggestion-filters';
import type { PainStatus } from '@/lib/types';

export interface DrillPlanSlot {
  drillId: string;
  sets: string;
  reps: string;
  rest: string;
  tempo: string;
  notes: string;
}

export interface DrillPlanDay {
  name: string;
  slots: Record<string, DrillPlanSlot>;
}

/**
 * Globally readable drill-plan definition stored in the `drill-plans` Firestore collection.
 */
export interface DrillPlan {
  id: string;
  name: string;
  description?: string;
  templateId: 'positional-intro' | 'small-drill-plan' | 'full';
  /** Phase at plan creation time — P1 for Introduction, P1–P3 for Full. */
  phase: 'P1' | 'P2' | 'P3';
  /** True if this is a twice-daily plan (Introduction template). */
  twiceDaily?: boolean;
  /** Searchable tags: phase string, lifting experience level, pain level, etc. */
  tags: string[];
  days: DrillPlanDay[];
  /** Human labels for each slot ID (e.g. 'Warm-Up-1' → 'Warm-Up 1'). */
  slotLabels: Record<string, string>;
  /** Snapshot of suggestion filters used when plan was auto-filled (replay stability). */
  suggestionFilterSnapshot?: DrillSuggestionFilters;
  createdBy: string;
  createdAt: unknown;
  updatedAt: unknown;
}

/**
 * Private assignment linking a client to a drill plan.
 * Only the `assigningPractitionerUid` practitioner may read or write this document.
 *
 * Stored at: `clients/{clientId}/drillPlanAssignments/{assignmentId}`
 */
export interface DrillPlanAssignment {
  id: string;
  /** References DrillPlan.id in the `drill-plans` collection. */
  drillPlanDefinitionId: string;
  clientId: string;
  /** Owning practitioner UID (read/write gated). */
  assigningPractitionerUid: string;
  /** Denormalized plan name for list views. */
  drillPlanName?: string;
  notes?: string;
  createdAt: unknown;
  updatedAt: unknown;
}

/**
 * Drill-plan tag per derived pain status (Mathias 2026-07-11): the plan keys
 * off the STATUS (Conor's tiers), not a re-thresholded raw number. no-pain +
 * minimal collapse into 'low-pain' (same partition as the old <=3 / <=6 NRS
 * thresholds); some/lots map 1:1 onto the existing tag vocabulary.
 */
const PAIN_STATUS_TO_PLAN_TAG: Record<PainStatus, string> = {
  'no-pain': 'low-pain',
  minimal: 'low-pain',
  some: 'moderate-pain',
  lots: 'high-pain',
};

/**
 * Build the canonical tags array for a new drill plan based on known semantics.
 *
 * `painStatus` is the status DERIVED from the client's 0–10 NRS pain level —
 * callers MUST pass the client-API-derived value (`deriveClientPain(client)
 * .painStatus`, or `Client.painStatus` off an API response), never a status
 * re-derived from raw database fields.
 */
export function buildDrillPlanTags(
  phase: 'P1' | 'P2' | 'P3',
  liftingExperience?: number,
  painStatus?: PainStatus,
  extra: string[] = []
): string[] {
  const tags: string[] = [phase];

  if (liftingExperience !== undefined) {
    if (liftingExperience <= 0) tags.push('beginner');
    else if (liftingExperience <= 1) tags.push('intermediate');
    else tags.push('advanced');
  }

  if (painStatus !== undefined) {
    tags.push(PAIN_STATUS_TO_PLAN_TAG[painStatus]);
  }

  return [...new Set([...tags, ...extra])];
}
